import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { Input, Select, Textarea, Button, useToast, T } from '../components/UI';

const MAKES = ['Any Make','Volkswagen','BMW','Audi','Toyota','Ford','Mercedes-Benz','Hyundai','Nissan','Kia','Skoda','Peugeot','Renault','Opel','Volvo','Honda','SEAT','Mazda','Suzuki','Mini','Fiat','Tesla','Land Rover','Jaguar','Jeep','Other'];
const FUEL_TYPES = ['Any','Petrol','Diesel','Electric','Petrol Hybrid','Diesel Hybrid','Plug-in Hybrid'];
const TRANSMISSIONS = ['Any','Manual','Automatic'];
const BUDGETS = ['Under €10,000','€10,000–€15,000','€15,000–€20,000','€20,000–€30,000','€30,000–€40,000','€40,000–€60,000','€60,000+'];
const YEARS = ['Any Year',...Array.from({length:12},(_,i)=>2025-i)];
const TIMELINES = ['As soon as possible','Within 1 month','Within 2–3 months','No rush'];

const STEPS = [
  { n:'01', h:'You tell us',  b:'Make, model, spec, colour, budget, timeline. The more detail the better — it helps us narrow the search immediately.' },
  { n:'02', h:'We search',    b:'Our team searches UK main dealers, independents and five major auction houses. We typically find matches within 48 hours.' },
  { n:'03', h:'You approve',  b:'We send you full photos, spec and a cost breakdown before any purchase. You have complete approval at every stage.' },
  { n:'04', h:'We deliver',   b:'We arrange purchase, transport to Ireland, VRT payment, NCT preparation and registration. You collect the keys.' },
];

const FAQS = [
  { q:'How long does the process take?',    a:'Typically 2–4 weeks from your request to collection, depending on how quickly we find the right car and VRT processing times.' },
  { q:'Are there upfront fees?',            a:'No upfront fees to start the search. We only charge once we\'ve found a car you\'re happy with and you decide to proceed.' },
  { q:'What extra costs are there?',        a:'VRT (Vehicle Registration Tax) varies by engine size and CO₂ emissions. We provide a complete cost breakdown — purchase price, VRT, transport — before you commit.' },
  { q:'Can I reject a car you find?',       a:'Absolutely. We send full details and photos before purchasing anything. You have complete approval at every stage.' },
  { q:'What if I want a very specific spec?','a':'That\'s exactly what this service is built for. The more specific you are, the better. We have access to thousands of UK cars.' },
];

export default function Import() {
  const { show, ToastContainer } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [form, setForm] = useState({ name:'', email:'', phone:'', make:'', model:'', year:'', maxBudget:'', colour:'', transmission:'', fuelType:'', timeline:'', notes:'' });
  const [errors, setErrors] = useState({});

  const set = k => e => { const v = typeof e === 'string' ? e : e.target.value; setForm(f => ({ ...f, [k]: v })); setErrors(er => ({ ...er, [k]:'' })); };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Valid email required';
    if (!form.phone.trim()) errs.phone = 'Required';
    if (!form.maxBudget) errs.maxBudget = 'Please select a budget';
    setErrors(errs);
    return !Object.keys(errs).length;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try { await api.sendImportRequest(form); setSubmitted(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }
    catch { show('Failed to submit. Please call us.', 'error'); }
    setLoading(false);
  };

  return (
    <div style={{ paddingTop: 68 }}>
      <ToastContainer />

      {/* Hero — split with atmosphere image */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: '60svh' }}>
        <div style={{ background: T.ink, padding: 'clamp(48px,8vw,100px) clamp(2rem,5vw,5rem)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <p style={{ fontSize: 'var(--step-xs)', color: T.accent, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 20 }}>Bespoke import service</p>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', fontWeight: 400, color: T.bg, lineHeight: 1.08, letterSpacing: '-0.02em', marginBottom: 22, maxWidth: '14ch' }}>
            Can't find it<br/>in stock?<br/><em style={{ color: T.accent }}>We'll find it.</em>
          </h1>
          <p style={{ fontSize: 'var(--step-body)', color: 'rgba(232,230,225,0.6)', lineHeight: 1.7, maxWidth: '40ch' }}>
            Tell us exactly what you want and we'll source it from the UK — right make, model, spec and colour.
          </p>
        </div>
        <div style={{ position: 'relative', overflow: 'hidden', background: T.surface }}>
          <img src="https://picsum.photos/id/112/1000/800" alt="Golden grass atmosphere — UK import journey" loading="lazy" width="1000" height="800"
            style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.7) saturate(0.8)', display: 'block' }} />
          <p style={{ position: 'absolute', bottom: 12, right: 14, fontSize: 10, color: 'rgba(232,230,225,0.4)', fontStyle: 'italic' }}>Photo: Zugr — Unsplash</p>
        </div>
      </div>

      {/* How it works */}
      <div style={{ background: T.surface, paddingBlock: 'clamp(60px,8vw,100px)', paddingInline: 'var(--margin)' }}>
        <div style={{ maxWidth: 1680, margin: '0 auto' }}>
          <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 16 }}>Simple process</p>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2rem,4vw,3.5rem)', fontWeight: 400, letterSpacing: '-0.02em', marginBottom: 56 }}>How it works</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
            {STEPS.map((s, i) => (
              <div key={i} style={{ background: T.bg, padding: '32px 28px', borderTop: `3px solid ${i === 0 ? T.accent : T.surface}` }}>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 'var(--step-head)', fontWeight: 400, color: T.accent, lineHeight: 1, marginBottom: 18 }}>{s.n}</div>
                <h3 style={{ fontSize: 'var(--step-lead)', fontFamily: 'Playfair Display, serif', fontWeight: 400, marginBottom: 12, color: T.ink, letterSpacing: '-0.01em' }}>{s.h}</h3>
                <p style={{ fontSize: 'var(--step-sm)', color: T.muted, lineHeight: 1.7 }}>{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form + FAQ */}
      <div style={{ paddingBlock: 'clamp(60px,8vw,100px)', paddingInline: 'var(--margin)' }}>
        <div style={{ maxWidth: 1680, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 380px', gap: 60, alignItems: 'start' }}>

          {/* Form */}
          <div>
            <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 16 }}>Get started</p>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.75rem,3.5vw,3rem)', fontWeight: 400, letterSpacing: '-0.02em', marginBottom: 8 }}>Request your car</h2>
            <p style={{ fontSize: 'var(--step-body)', color: T.muted, marginBottom: 40, maxWidth: '44ch' }}>Fill in as much detail as you can. The more specific you are, the faster we can find a match.</p>

            {submitted ? (
              <div style={{ border: `1px solid ${T.accent}`, padding: '48px 40px', textAlign: 'center' }}>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2rem,4vw,3.5rem)', fontWeight: 400, color: T.accent, marginBottom: 16 }}>✓</div>
                <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'var(--step-head)', fontWeight: 400, marginBottom: 14 }}>Request received</h3>
                <p style={{ fontSize: 'var(--step-body)', color: T.muted, lineHeight: 1.7, marginBottom: 28, maxWidth: '38ch', margin: '0 auto 28px' }}>
                  Thanks <strong style={{ color: T.ink }}>{form.name}</strong>. We'll be in touch within a few hours to discuss your search.
                </p>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Link to="/stock"><Button variant="dark">Browse current stock</Button></Link>
                  <Link to="/contact"><Button variant="outline">Contact us</Button></Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {/* Contact details */}
                <div style={{ borderTop: `1px solid ${T.surface}`, paddingTop: 28, marginBottom: 28 }}>
                  <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 20 }}>Your details</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <Input label="Full name *" placeholder="Seán Murphy" value={form.name} onChange={set('name')} error={errors.name} />
                    <Input label="Phone *" placeholder="+353 85 …" value={form.phone} onChange={set('phone')} type="tel" error={errors.phone} />
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <Input label="Email *" placeholder="you@example.com" value={form.email} onChange={set('email')} type="email" error={errors.email} />
                  </div>
                </div>
                {/* Car spec */}
                <div style={{ borderTop: `1px solid ${T.surface}`, paddingTop: 28, marginBottom: 28 }}>
                  <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 20 }}>Car details</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <Select label="Make" value={form.make} onChange={set('make')}>{MAKES.map(m => <option key={m}>{m}</option>)}</Select>
                    <Input label="Model" placeholder="e.g. Golf, X5, Qashqai" value={form.model} onChange={set('model')} />
                    <Select label="Year" value={form.year} onChange={set('year')}>{YEARS.map(y => <option key={y}>{y}</option>)}</Select>
                    <Input label="Preferred colour" placeholder="e.g. Black, Silver, Any" value={form.colour} onChange={set('colour')} />
                    <Select label="Fuel type" value={form.fuelType} onChange={set('fuelType')}>{FUEL_TYPES.map(f => <option key={f}>{f}</option>)}</Select>
                    <Select label="Transmission" value={form.transmission} onChange={set('transmission')}>{TRANSMISSIONS.map(t => <option key={t}>{t}</option>)}</Select>
                  </div>
                </div>
                {/* Budget & timeline */}
                <div style={{ borderTop: `1px solid ${T.surface}`, paddingTop: 28, marginBottom: 28 }}>
                  <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 20 }}>Budget & timeline</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <Select label="Maximum budget *" value={form.maxBudget} onChange={set('maxBudget')} error={errors.maxBudget}>
                      <option value="">Select budget</option>
                      {BUDGETS.map(b => <option key={b}>{b}</option>)}
                    </Select>
                    <Select label="Timeline" value={form.timeline} onChange={set('timeline')}>
                      <option value="">Select timeline</option>
                      {TIMELINES.map(t => <option key={t}>{t}</option>)}
                    </Select>
                  </div>
                </div>
                <div style={{ marginBottom: 28 }}>
                  <Textarea label="Additional notes / specific requirements" placeholder="e.g. Must have service history, panoramic roof, under 50,000km. Happy to consider 2019 or 2020." value={form.notes} onChange={set('notes')} style={{ minHeight: 110 }} />
                </div>
                <Button type="submit" size="lg" fullWidth loading={loading}>Submit import request</Button>
                <p style={{ fontSize: 'var(--step-xs)', color: T.muted, textAlign: 'center', marginTop: 12, letterSpacing: '0.04em' }}>No upfront fees · We'll contact you within a few hours</p>
              </form>
            )}
          </div>

          {/* FAQ sidebar */}
          <div style={{ position: 'sticky', top: 90 }}>
            <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'var(--step-lead)', fontWeight: 400, marginBottom: 24, letterSpacing: '-0.01em' }}>Common questions</h3>
            {FAQS.map((faq, i) => (
              <div key={i} style={{ borderTop: `1px solid ${T.surface}` }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{ width: '100%', padding: '16px 0', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, textAlign: 'left', fontFamily: 'inherit' }}>
                  <span style={{ fontSize: 'var(--step-sm)', fontWeight: 500, color: T.ink, lineHeight: 1.4 }}>{faq.q}</span>
                  <span style={{ color: T.accent, fontSize: 14, flexShrink: 0, marginTop: 2 }}>{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && <div style={{ paddingBottom: 16, fontSize: 'var(--step-sm)', color: T.muted, lineHeight: 1.7 }}>{faq.a}</div>}
              </div>
            ))}
            <div style={{ borderTop: `1px solid ${T.surface}`, paddingTop: 28, marginTop: 8 }}>
              <p style={{ fontSize: 'var(--step-sm)', color: T.muted, marginBottom: 16, lineHeight: 1.6 }}>Prefer to talk it through first?</p>
              <a href="tel:+353851234567" style={{ display: 'block', background: T.ink, color: T.bg, padding: '13px 20px', fontSize: 'var(--step-xs)', fontWeight: 600, textAlign: 'center', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8, textDecoration: 'none', transition: 'background 0.15s' }}
                onMouseEnter={e=>e.currentTarget.style.background='#333'} onMouseLeave={e=>e.currentTarget.style.background=T.ink}>
                Call +353 85 123 4567
              </a>
              <a href="https://wa.me/353851234567" target="_blank" rel="noreferrer"
                style={{ display: 'block', background: '#25D366', color: '#fff', padding: '13px 20px', fontSize: 'var(--step-xs)', fontWeight: 600, textAlign: 'center', letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration: 'none', transition: 'opacity 0.15s' }}
                onMouseEnter={e=>e.currentTarget.style.opacity='0.85'} onMouseLeave={e=>e.currentTarget.style.opacity='1'}>
                WhatsApp us
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
